<script setup>
import OrderProgress from '@/pages/order/components/OrderProgress.vue'
import { useSelfOrderStore } from '@/stores/selfOrder'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()
const store = useSelfOrderStore()

const customerType = ref('guest') // 'guest' | 'member'
const isSubmitting = ref(false)
const errorMessage = ref('')
const fieldErrors = ref({})
const isPasswordVisible = ref(false)

const form = ref({
  name: '',
  phone: '',
  password: '',
})

const submit = async () => {
  isSubmitting.value = true
  errorMessage.value = ''
  fieldErrors.value = {}

  try {
    await store.identify({
      type: customerType.value,
      name: form.value.name || undefined,
      phone: form.value.phone,
      password: customerType.value === 'member' ? form.value.password : undefined,
    })

    router.replace({ name: 'order-session-token-menu', params: { sessionToken: route.params.sessionToken } })
  }
  catch (err) {
    if (err?.status === 422 || err?.response?.status === 422)
      fieldErrors.value = err?.data?.errors || {}

    errorMessage.value = err?.data?.message || err?.message || 'Gagal memproses. Periksa kembali data Anda.'
  }
  finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="identify-screen">
    <div class="identify-hero">
      <div class="identify-blob" />
      <div class="identify-hero-content pt-8 pb-12 px-4 text-center">
        <div class="identify-badge mb-4">
          <VIcon icon="ri-cup-line" size="28" />
          <span class="identify-steam identify-steam--a" />
          <span class="identify-steam identify-steam--b" />
        </div>
        <h4 class="text-h4 font-weight-bold text-white mb-1">
          Selamat Datang 👋
        </h4>
        <p class="text-body-2 identify-hero-subtitle mb-0">
          {{ store.tableLabel }} — isi data Anda untuk mulai memesan
        </p>
      </div>
    </div>

    <VContainer class="identify-body">
      <VCard class="identify-card pa-4 pa-sm-7 mx-auto" max-width="440" elevation="0">
        <OrderProgress :step="1" class="mb-4" />

        <div class="identify-segmented mb-6">
          <button
            type="button"
            class="identify-segment"
            :class="{ 'is-active': customerType === 'guest' }"
            @click="customerType = 'guest'"
          >
            <VIcon icon="ri-user-line" size="16" start />
            Tamu
          </button>
          <button
            type="button"
            class="identify-segment"
            :class="{ 'is-active': customerType === 'member' }"
            @click="customerType = 'member'"
          >
            <VIcon icon="ri-vip-crown-2-line" size="16" start />
            Member
          </button>
        </div>

        <VAlert v-if="errorMessage" type="error" variant="tonal" class="mb-4" rounded="lg" closable @click:close="errorMessage = ''">
          {{ errorMessage }}
        </VAlert>

        <VForm @submit.prevent="submit">
          <VRow>
            <VCol v-if="customerType === 'guest'" cols="12">
              <VTextField
                v-model="form.name"
                autofocus
                label="Nama"
                placeholder="Nama Anda"
                prepend-inner-icon="ri-user-line"
                rounded="lg"
                variant="outlined"
                :error-messages="fieldErrors.name"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.phone"
                label="No. HP"
                placeholder="08123456789"
                type="tel"
                prepend-inner-icon="ri-smartphone-line"
                rounded="lg"
                variant="outlined"
                :error-messages="fieldErrors.phone"
              />
            </VCol>

            <VCol v-if="customerType === 'member'" cols="12">
              <VTextField
                v-model="form.password"
                label="Password"
                placeholder="············"
                :type="isPasswordVisible ? 'text' : 'password'"
                prepend-inner-icon="ri-lock-line"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                rounded="lg"
                variant="outlined"
                :error-messages="fieldErrors.password"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
            </VCol>

            <VCol cols="12">
              <VBtn block size="large" type="submit" rounded="lg" :loading="isSubmitting">
                {{ customerType === 'guest' ? 'Mulai Pesan' : 'Masuk & Mulai Pesan' }}
                <VIcon icon="ri-arrow-right-line" end />
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCard>
    </VContainer>
  </div>
</template>

<style scoped>
.identify-screen {
  min-block-size: 100vh;
  background: rgb(var(--v-theme-background));
}

.identify-hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
}

.identify-blob {
  position: absolute;
  inset-block-start: -80px;
  inset-inline-end: -60px;
  inline-size: 220px;
  block-size: 220px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

.identify-hero-content {
  position: relative;
  z-index: 1;
}

.identify-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
}

.identify-badge {
  position: relative;
  inline-size: 56px;
  block-size: 56px;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.16);
  color: white;
}

.identify-steam {
  position: absolute;
  inset-block-start: -10px;
  inline-size: 3px;
  block-size: 10px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.6);
  animation: identify-steam-rise 2.2s ease-in-out infinite;
}

.identify-steam--a { inset-inline-start: 20px; animation-delay: 0s; }
.identify-steam--b { inset-inline-start: 32px; animation-delay: .6s; }

@keyframes identify-steam-rise {
  0% { transform: translateY(0) scaleY(1); opacity: 0; }
  30% { opacity: .8; }
  100% { transform: translateY(-14px) scaleY(1.4); opacity: 0; }
}

.identify-body {
  margin-block-start: -48px;
  position: relative;
  z-index: 2;
}

.identify-card {
  border-radius: 24px;
  box-shadow: 0 24px 48px -24px rgba(var(--v-theme-on-surface), 0.25);
}

.identify-segmented {
  display: flex;
  padding: 4px;
  border-radius: 14px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  gap: 4px;
}

.identify-segment {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-block: 10px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.6);
  transition: background-color .2s ease, color .2s ease, box-shadow .2s ease;
}

.identify-segment.is-active {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-primary));
  box-shadow: 0 2px 8px rgba(var(--v-theme-on-surface), 0.12);
}

@media (prefers-reduced-motion: reduce) {
  .identify-steam { animation: none; }
}
</style>
